import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bug, Lightbulb, Heart, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { appConfig } from '../../config/app.config';

export function FeedbackModal({ userInfo }) {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  // Only render in alpha version
  if (!appConfig.features.feedbackModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const response = await fetch(`${appConfig.apiUrl}/api/alpha/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userInfo?.userId,
          email: userInfo?.email,
          feedbackType,
          message,
          userAgent: navigator.userAgent,
          url: window.location.href,
          screenResolution: `${window.screen.width}x${window.screen.height}`
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          setMessage('');
          setIsOpen(false);
          setSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to send feedback:', error);
    } finally {
      setSending(false);
    }
  };

  const feedbackTypes = [
    { 
      id: 'bug', 
      label: 'Bug Report', 
      icon: Bug, 
      color: 'text-red-400',
      description: 'Something isn\'t working' 
    },
    { 
      id: 'feature', 
      label: 'Feature Request', 
      icon: Lightbulb, 
      color: 'text-yellow-400',
      description: 'Suggest improvements' 
    },
    { 
      id: 'ui', 
      label: 'UI/UX', 
      icon: Zap, 
      color: 'text-blue-400',
      description: 'Design feedback' 
    },
    { 
      id: 'general', 
      label: 'General', 
      icon: Heart, 
      color: 'text-pink-400',
      description: 'Other feedback' 
    }
  ];

  const selectedType = feedbackTypes.find(type => type.id === feedbackType);

  return (
    <>
      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-40"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
      >
        <motion.button
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-shadow group relative"
          onClick={() => setIsOpen(true)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <MessageSquare className="h-6 w-6" />
          
          {/* Alpha badge */}
          <motion.span 
            className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.2, type: "spring" }}
          >
            FEEDBACK
          </motion.span>

          {/* Tooltip */}
          <motion.div
            className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            initial={{ y: 10, opacity: 0 }}
            whileHover={{ y: 0, opacity: 1 }}
          >
            Share your feedback
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => !sending && setIsOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal Content */}
            <motion.div
              className="relative bg-[#2a282a] rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
            >
              {/* Header */}
              <div className="p-6 border-b border-[#494646]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-100">
                        Alpha Feedback
                      </h2>
                      <p className="text-sm text-gray-400">
                        Help us improve DigiTables
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-200 transition-colors p-1"
                    disabled={sending}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6">
                {/* Feedback Type Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    What type of feedback do you have?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {feedbackTypes.map((type) => (
                      <motion.button
                        key={type.id}
                        type="button"
                        onClick={() => setFeedbackType(type.id)}
                        className={`p-4 rounded-lg border transition-all text-left ${
                          feedbackType === type.id
                            ? 'border-purple-500 bg-purple-500/10 ring-2 ring-purple-500/20'
                            : 'border-[#494646] hover:border-gray-500 hover:bg-[#333]'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-start gap-3">
                          <type.icon className={`h-5 w-5 mt-0.5 ${type.color}`} />
                          <div>
                            <div className="font-medium text-gray-200 text-sm">
                              {type.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {type.description}
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <selectedType.icon className={`h-4 w-4 ${selectedType.color}`} />
                    <label className="text-sm font-medium text-gray-300">
                      Tell us more about your {selectedType.label.toLowerCase()}
                    </label>
                  </div>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      feedbackType === 'bug' 
                        ? "What happened? What did you expect to happen? Steps to reproduce..."
                        : feedbackType === 'feature'
                        ? "What feature would you like to see? How would it help you?"
                        : feedbackType === 'ui'
                        ? "What could be improved about the design or user experience?"
                        : "Share your thoughts, suggestions, or questions..."
                    }
                    rows={6}
                    required
                    maxLength={2000}
                    className="w-full px-3 py-3 bg-[#151415] border border-[#494646] rounded-lg text-gray-200 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none placeholder-gray-500"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <div className="text-xs text-gray-500">
                      {message.length}/2000 characters
                    </div>
                    {message.length > 1900 && (
                      <div className="text-xs text-orange-400">
                        {2000 - message.length} characters left
                      </div>
                    )}
                  </div>
                </div>

                {/* Alpha Notice */}
                <motion.div 
                  className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-orange-300">
                      <p className="font-medium mb-1">Alpha Version Feedback</p>
                      <p className="leading-relaxed">
                        Your feedback is invaluable! We read every submission and use it to improve DigiTables. 
                        As an alpha user, you're helping shape the future of the platform.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={sending || !message.trim() || success}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Sending feedback...
                    </div>
                  ) : success ? (
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Thank you! Feedback sent
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Send Feedback
                    </div>
                  )}
                </Button>

                {/* User Info (for debugging) */}
                {userInfo && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Logged in as {userInfo.email}
                  </div>
                )}
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}